import React, { useCallback, useEffect, useState } from "react";
import { api, apiFile } from "../../../api";
import { noDash } from "./leadModel.mjs";

/*
 * THE AGREEMENT BUILDER, inside the lead block (Pricing sent and Agreement + legal),
 * collapsed under "▸ Agreement builder · .docx from what we quoted" (frame 1d).
 *
 * GenerateAgreement.js's logic, kept whole, in the record's grammar: the draft, the
 * eleven fields each marked in writing / inferred / not in record with where it came
 * from, each overridable in place and restorable, the blocked state that explains
 * itself, and the generated state with what went out blank on purpose. NOTHING IS
 * DECIDED HERE: tiers, counts, the status and what blocks all arrive from
 * GET /companies/:id/agreement (src/agreement/fields.js), so the CLI and the record
 * never disagree about whether a deal is ready.
 *
 * Three things change from the rail card it replaces, all house style:
 *   - Generate is BLACK. The record's one yellow button is in Next, and this opens
 *     under a fold the reader chose to open (the brief: confirming buttons inside
 *     what something else opened are black).
 *   - The status reads as a neutral outline tag, never a coloured pill.
 *   - The server's citations and warnings were written with em dashes; they are
 *     printed through noDash, so no copy on the record carries one.
 * Fetched only once opened: the draft reads the mailbox for the pricing email, and
 * there is no reason to do that on every record load.
 *
 * Props:
 *   company   the company (id, name, status, pipeline_stage)
 *   onChanged called after a saved override, so the record reloads
 */

// src/agreement/fields.js renders eleven rows for every agreement, whatever the deal.
export const AGREEMENT_FIELDS = 11;

const TIER_MARK = { writing: "☑", inferred: "⊡", blank: "☐" };
const TIER_LABEL = { writing: "in writing", inferred: "inferred", blank: "not in record" };

/** Where the builder applies: the stage between pricing and signature, prospects only. */
export function builderShows(company) {
  if (!company || company.status !== "prospect") return false;
  return company.pipeline_stage === "agreement" || company.pipeline_stage === "proposal_sent";
}

export default function AgreementBuilder({ company, onChanged }) {
  const [draft, setDraft] = useState(null);
  const [table, setTable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(null);

  const load = useCallback(async () => {
    try {
      setDraft(await api(`/companies/${company.id}/agreement`));
    } catch (e) {
      setError(e.message);
    }
  }, [company.id]);

  useEffect(() => {
    if (builderShows(company)) load();
  }, [company, load]);

  if (!builderShows(company)) return <div className="rec-empty">The builder opens at Pricing sent and Agreement + legal.</div>;
  if (!draft) return error ? <span className="rec-err">{error}</span> : <div className="rec-meta">Reading what we quoted…</div>;

  const blocking = draft.fields.filter((f) => f.blocking);
  const canGenerate = !draft.check.errors.length;

  async function save(patch) {
    setBusy(true);
    setError(null);
    try {
      const terms = { ...draft.terms, ...patch, client: { ...draft.terms.client, ...(patch.client || {}) } };
      await api(`/companies/${company.id}/agreement`, { method: "PUT", body: JSON.stringify({ terms }) });
      await load();
      if (onChanged) await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Restore is a DELETE of the override, not an undo: the value falls back to what
  // the record says, so there is no history to keep.
  async function restore(key) {
    const terms = { ...draft.terms };
    if (key === "base_fee" || key === "spend_bands") terms.fee = draft.derived.fee;
    else if (key === "legal_entity") terms.client = { ...terms.client, company: draft.derived.client.company };
    else if (key === "client") terms.client = { ...terms.client, display_name: draft.derived.client.display_name };
    else terms[key] = draft.derived[key];
    await save(terms);
  }

  async function generate({ allowBlank = false } = {}) {
    setBusy(true);
    setError(null);
    try {
      const overrides = allowBlank ? { allow_blank: ["legal_entity"] } : {};
      setGenerated(
        await apiFile(`/companies/${company.id}/agreement/document`, { method: "POST", body: JSON.stringify({ terms: overrides }) })
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (generated) return <Generated file={generated} draft={draft} onRegenerate={() => setGenerated(null)} />;

  const head = draft.headline || {};
  const counts = draft.tally || {};
  return (
    <div className="rec-agr">
      <div className="rec-agr__top">
        <span className="rec-tag">{draft.status && draft.status.label}</span>
        <span className="rec-meta">
          {["writing", "inferred", "blank"].map((t) => `${TIER_MARK[t]} ${counts[t] || 0} ${t === "blank" ? "blank" : TIER_LABEL[t]}`).join("  ")}
          {counts.overrides > 0 ? `  ✎ ${counts.overrides} override` : ""}
        </span>
      </div>
      {head.money ? (
        <div>
          <div className="rec-strong">
            {head.money}
            {head.bands ? <span className="rec-sub"> {noDash(head.bands)}</span> : null}
          </div>
          {head.detail && <div className="rec-sub">{noDash(head.detail)}</div>}
        </div>
      ) : (
        <div className="rec-empty">No readable fee on this record yet</div>
      )}

      {table && (
        <div className="rec-agr__table">
          {draft.fields.map((field) => (
            <Row key={field.key} field={field} draft={draft} busy={busy} onSet={save} onRestore={() => restore(field.key)} />
          ))}
          {draft.tally_sentence && <div className="rec-meta">{noDash(draft.tally_sentence)}</div>}
        </div>
      )}

      {blocking.length > 0 && <Blocked draft={draft} blocking={blocking} busy={busy} onAnyway={() => generate({ allowBlank: true })} />}

      <div className="rec-actionsrow">
        <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy || !canGenerate} onClick={() => generate()}>
          {busy ? "Working…" : "Generate agreement"}
        </button>
        <button type="button" className="rec-link rec-lead__link" onClick={() => setTable((v) => !v)}>
          {table ? "Hide the fields" : `Review ${(counts.total || AGREEMENT_FIELDS)} fields`}
        </button>
        {!canGenerate && blocking.length > 0 && (
          <span className="rec-meta">{blocking.length} required field{blocking.length === 1 ? "" : "s"} missing</span>
        )}
        {error && <span className="rec-err">{error}</span>}
      </div>
    </div>
  );
}

/*
 * One field: label, value (editable in place: a text input, the two papers as a
 * segmented choice), and the citation under it, because the whole reason the table
 * is worth reading is that where a figure came from sits beside it.
 */
function Row({ field, draft, busy, onSet, onRestore }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const patchFor = (raw) => {
    if (field.key === "legal_entity") return { client: { company: raw } };
    if (field.key === "client") return { client: { display_name: raw } };
    if (field.key === "base_fee") {
      const base = Number(String(raw).replace(/[^\d.]/g, ""));
      return Number.isFinite(base) && base > 0 ? { fee: { ...draft.terms.fee, base } } : null;
    }
    if (field.key === "monthly_budget") {
      const n = Number(String(raw).replace(/[^\d.]/g, ""));
      return { monthly_budget: Number.isFinite(n) && n > 0 ? n : null };
    }
    return { [field.key]: raw };
  };
  const commit = async () => {
    const patch = patchFor(value.trim());
    setEditing(false);
    if (patch) await onSet(patch);
  };

  return (
    <div className={`rec-agr__row${field.blocking ? " is-blocking" : ""}`}>
      <span className="rec-agr__label">{field.label}</span>
      <span className="rec-agr__val">
        {field.choice && !field.blank ? (
          <span className="rec-seg">
            {field.choice.map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={busy}
                className={opt.value === String(field.value) ? "is-on" : ""}
                onClick={() => opt.value !== String(field.value) && onSet(patchFor(opt.value))}
              >
                {opt.label}
              </button>
            ))}
          </span>
        ) : editing ? (
          <span className="rec-editrow">
            <input
              className="rec-input"
              autoFocus
              value={value}
              disabled={busy}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
              }}
            />
            <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy} onClick={commit}>Save</button>
            <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" onClick={() => setEditing(false)}>Cancel</button>
          </span>
        ) : (
          <button
            type="button"
            className={`rec-agr__value${field.blank ? " is-blank" : ""}`}
            title="Edit"
            onClick={() => {
              setValue(field.blank ? "" : String(field.money ? field.value : field.display));
              setEditing(true);
            }}
          >
            {field.blank ? noDash(field.blank_label) : noDash(field.display)} <span className="rec-pencil" aria-hidden="true">✎</span>
          </button>
        )}
        {field.overridden && (
          <span className="rec-meta">
            ✎ override ·{" "}
            <button type="button" className="rec-link rec-lead__link" disabled={busy} onClick={onRestore}>restore</button>
          </span>
        )}
        {field.help && <span className="rec-meta">{noDash(field.help)}</span>}
        <span className="rec-agr__cite">
          {TIER_MARK[field.tier]} {TIER_LABEL[field.tier]} · {noDash(field.blank ? `${field.cite}, ${field.blank_why}` : field.cite)}
          {field.blocking && <b> · blocks generation</b>}
        </span>
      </span>
    </div>
  );
}

/*
 * The blocked state EXPLAINS rather than announces: why the field is not optional,
 * who handled the proposal, and the way past it if the answer is not coming today.
 * The words are red (a warning); the box is not, so red stays the warning's colour.
 */
function Blocked({ draft, blocking, busy, onAnyway }) {
  const field = blocking[0];
  const ask = draft.ask;
  const mailto = ask
    ? `mailto:${ask.email}?subject=${encodeURIComponent(`${draft.company.name}: registered company name for the agreement`)}`
    : null;
  return (
    <div className="rec-evidence">
      <span className="rec-warn">Can’t generate yet</span>
      <span>
        {noDash(field.blocked_why)}
        {ask && (
          <>
            {" "}
            {ask.name}
            {ask.title ? ` (${ask.title})` : ""} handled the proposal.
          </>
        )}
      </span>
      <span className="rec-inline">
        {mailto && <a href={mailto}>Ask {String(ask.name || "them").split(" ")[0]} for it ↗</a>}
        <button type="button" className="rec-link rec-lead__link" disabled={busy} onClick={onAnyway}>
          Generate anyway, leave it blank for {draft.company.name} to fill
        </button>
      </span>
    </div>
  );
}

/* What went out: the file, what was left blank on purpose, and the terms used. */
function Generated({ file, draft, onRegenerate }) {
  const summary = file.summary || {};
  const tally = draft.tally || {};
  const template = (draft.templates || []).find((t) => t.id === summary.template);
  return (
    <div className="rec-agr">
      <div className="rec-agr__top">
        <span className="rec-tag">generated</span>
        <button type="button" className="rec-link rec-lead__link" onClick={onRegenerate}>Regenerate</button>
      </div>
      <div>
        <div className="rec-strong">{file.filename}</div>
        <div className="rec-sub">
          {(template && template.length) || summary.template || "agreement"} · generated just now · {(tally.total || 0) - (tally.blank || 0)} of {tally.total || AGREEMENT_FIELDS} fields filled ·{" "}
          <a href={file.url} download={file.filename}>Download</a>
        </div>
      </div>
      {summary.left_blank && summary.left_blank.length > 0 && (
        <div className="rec-lead__sec">
          <span className="rec-lead__small">Left blank on purpose</span>
          {summary.left_blank.map((b) => (
            <span key={b.field}>☐ <b>{b.label}</b>, {noDash(b.why)}</span>
          ))}
        </div>
      )}
      {summary.terms_used && (
        <div className="rec-lead__sec">
          <span className="rec-lead__small">Terms used</span>
          <div className="rec-kv rec-kv--150">
            {summary.terms_used.map(([label, value]) => (
              <React.Fragment key={label}>
                <span>{label}</span>
                <span>{noDash(value)}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      <div className="rec-meta">
        {file.warnings && file.warnings.length
          ? `${file.warnings.length} thing${file.warnings.length === 1 ? "" : "s"} worth knowing before it goes out:`
          : "No overrides. Every figure matches the record."}
      </div>
      {file.warnings && file.warnings.length > 0 && (
        <ul className="rec-lead__points">
          {file.warnings.map((w) => (
            <li key={w}>{noDash(w)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
